import { Actions, Effect, ofType } from '@ngrx/effects';
import {
  DealMetricsActionTypes,
  LoadDealMetrics,
  LoadDealMetricsFail,
  LoadDealMetricsSuccess,
  SaveScore,
  SaveScoreFail,
  SaveScoreSuccess,
  SaveRatingFail,
  SaveRatingSuccess,
  SaveRating,
  SaveOwnershipFail,
  SaveOwnershipSuccess,
  SaveStatusSuccess,
  SaveStatusFail,
  SaveStatus,
  SaveOwnership
} from './actions';
import { of, Observable } from 'rxjs';
import {
  switchMap,
  map,
  catchError,
  withLatestFrom,
  pluck,
  pairwise
} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { DealMetricsService } from '../../services/deal-metrics.service';
import { Store, select } from '@ngrx/store';
import { selectDealMetrics } from '../reducer';

@Injectable()
export class DealMetricsEffects {
  @Effect()
  public fetchDealMetrics$: Observable<
    LoadDealMetricsSuccess | LoadDealMetricsFail
  > = this.actions$.pipe(
    ofType(DealMetricsActionTypes.Load),
    switchMap((action: LoadDealMetrics) =>
      this.dealMetricsService.getDealMetrics(action.payload).pipe(
        map((metrics) => {
          if (metrics && metrics.internalStatus) {
            metrics.internalStatus = metrics.internalStatus.replace(/\s/g, '');
          }
          return new LoadDealMetricsSuccess(metrics);
        }),
        catchError((err) => of(new LoadDealMetricsFail(err)))
      )
    )
  );

  @Effect()
  public saveScore$: Observable<
    SaveScoreSuccess | SaveScoreFail
  > = this.actions$.pipe(
    ofType(DealMetricsActionTypes.SaveScore),
    withLatestFrom(
      this.store.pipe(select(selectDealMetrics), pluck('score'), pairwise())
    ),
    switchMap(
      ([
        {
          payload: { dealId, score }
        },
        [prevScore]
      ]: [SaveScore, [string, string]]) =>
        this.dealMetricsService.saveScore(dealId, score).pipe(
          map(() => new SaveScoreSuccess()),
          catchError((err) =>
            of(new SaveScoreFail({ dealId, score: prevScore, error: err }))
          )
        )
    )
  );

  @Effect()
  public saveRating$: Observable<
    SaveRatingSuccess | SaveRatingFail
  > = this.actions$.pipe(
    ofType(DealMetricsActionTypes.SaveRating),
    withLatestFrom(
      this.store.pipe(select(selectDealMetrics), pluck('rating'), pairwise())
    ),
    switchMap(
      ([
        {
          payload: { dealId, rating }
        },
        [prevRating]
      ]: [SaveRating, [string, string]]) =>
        this.dealMetricsService.saveRating(dealId, rating).pipe(
          map(() => new SaveRatingSuccess()),
          catchError((err) =>
            of(new SaveRatingFail({ dealId, rating: prevRating, error: err }))
          )
        )
    )
  );

  @Effect()
  public saveOwnership$: Observable<
    SaveOwnershipSuccess | SaveOwnershipFail
  > = this.actions$.pipe(
    ofType(DealMetricsActionTypes.SaveOwnership),
    withLatestFrom(
      this.store.pipe(select(selectDealMetrics), pluck('ownership'), pairwise())
    ),
    switchMap(
      ([
        {
          payload: { dealId, ownership }
        },
        [prevOwnership]
      ]: [SaveOwnership, [boolean, boolean]]) =>
        this.dealMetricsService.saveOwnership(dealId, ownership).pipe(
          map(() => new SaveOwnershipSuccess()),
          catchError((err) =>
            of(
              new SaveOwnershipFail({
                dealId,
                ownership: prevOwnership,
                error: err
              })
            )
          )
        )
    )
  );

  @Effect()
  public saveStatus$: Observable<
    SaveStatusSuccess | SaveStatusFail
  > = this.actions$.pipe(
    ofType(DealMetricsActionTypes.SaveStatus),
    withLatestFrom(
      this.store.pipe(select(selectDealMetrics), pluck('status'), pairwise())
    ),
    switchMap(
      ([
        {
          payload: { dealId, status }
        },
        [prevStatus]
      ]: [SaveStatus, [string, string]]) =>
        this.dealMetricsService.saveStatus(dealId, status).pipe(
          map(() => new SaveStatusSuccess()),
          catchError((err) =>
            of(new SaveStatusFail({ dealId, status: prevStatus, error: err }))
          )
        )
    )
  );

  constructor(
    private actions$: Actions,
    private dealMetricsService: DealMetricsService,
    private store: Store<Record<string, unknown>>
  ) {}
}
